"use server"

/**
 * Cart Server Actions
 *
 * Wrapper actions for cart operations that provide better error handling
 * and toast notifications support.
 */

import { addToCart as addToCartData, addRentalToCart, updateLineItem, deleteLineItem, retrieveCart, updateRentalLineItem } from '@lib/data/cart'
import type { ErrorParams } from '@lib/util/medusa-error'

export interface CartActionResult {
  success: boolean
  error?: string
  /** Stable error code from medusa-error.ts so the UI can render
   *  t(`cart.errors.${errorCode}`) instead of raw English text. */
  errorCode?: string
  /** Interpolation values for the coded message (e.g. `{ count }`). */
  errorParams?: ErrorParams
  cart?: any
}

/**
 * Server actions must RETURN errors, not throw them: Next.js replaces the
 * message of an error thrown from a server action with a generic one in
 * production, which would drop errorCode on the floor. Every catch below
 * goes through this helper so `errorCode`/`errorParams` reach the client.
 */
function extractErrorPayload(
  error: unknown,
  fallback: string
): { error: string; errorCode?: string; errorParams?: ErrorParams } {
  if (error instanceof Error) {
    const e = error as Error & { errorCode?: string; errorParams?: ErrorParams }
    return { error: error.message || fallback, errorCode: e.errorCode, errorParams: e.errorParams }
  }
  return { error: typeof error === "string" ? error : fallback }
}

/**
 * Add item to cart
 * T065: Create addToCart Server Action
 */
export async function addToCartAction({
  variantId,
  quantity,
  countryCode,
}: {
  variantId: string
  quantity: number
  countryCode: string
}): Promise<CartActionResult> {
  try {
    await addToCartData({ variantId, quantity, countryCode })
    const cart = await retrieveCart()

    return {
      success: true,
      cart,
    }
  } catch (error) {
    console.error('Failed to add item to cart:', error)
    return { success: false, ...extractErrorPayload(error, 'Failed to add item to cart') }
  }
}

/**
 * Update cart item quantity
 * T066: Create updateCartItem Server Action
 */
export async function updateCartItemAction({
  lineId,
  quantity,
}: {
  lineId: string
  quantity: number
}): Promise<CartActionResult> {
  try {
    await updateLineItem({ lineId, quantity })
    const cart = await retrieveCart()

    return {
      success: true,
      cart,
    }
  } catch (error) {
    console.error('Failed to update cart item:', error)
    return { success: false, ...extractErrorPayload(error, 'Failed to update cart item') }
  }
}

/**
 * Remove item from cart
 * T067: Create removeCartItem Server Action
 */
export async function removeCartItemAction(lineId: string): Promise<CartActionResult> {
  try {
    await deleteLineItem(lineId)
    const cart = await retrieveCart()

    return {
      success: true,
      cart,
    }
  } catch (error) {
    console.error('Failed to remove cart item:', error)
    return { success: false, ...extractErrorPayload(error, 'Failed to remove cart item') }
  }
}

/**
 * Retrieve current cart
 */
export async function getCartAction(): Promise<CartActionResult> {
  try {
    const cart = await retrieveCart()

    return {
      success: true,
      cart,
    }
  } catch (error) {
    console.error('Failed to retrieve cart:', error)
    return { success: false, ...extractErrorPayload(error, 'Failed to retrieve cart') }
  }
}

/**
 * Update rental item in cart (duration, start date)
 */
export async function updateRentalItemAction({
  lineId,
  rentalPeriod,
  rentalUnit,
  startDate,
}: {
  lineId: string
  rentalPeriod?: number
  rentalUnit?: "day" | "week" | "month"
  startDate?: string
}): Promise<CartActionResult & { rentalMetadata?: any }> {
  try {
    const response = await updateRentalLineItem({ lineId, rentalPeriod, rentalUnit, startDate })
    const cart = await retrieveCart()

    return {
      success: true,
      cart,
      rentalMetadata: response?.rentalMetadata,
    }
  } catch (error) {
    console.error('Failed to update rental item:', error)
    return { success: false, ...extractErrorPayload(error, 'Failed to update rental item') }
  }
}

/**
 * Add a RENTAL line item. Same result-returning contract as
 * addToCartAction — the coded refusal (outOfStock / insufficientInventory
 * / productAlreadyInCartAsSale ...) survives the server-action boundary.
 */
export async function addRentalToCartAction(params: {
  variantId: string
  rentalPeriod: number
  rentalUnit: string
  startDate: string
  quantity: number
  countryCode: string
}): Promise<CartActionResult & { rentalMetadata?: any }> {
  try {
    const response = await addRentalToCart(params)
    return {
      success: true,
      cart: response?.cart,
      rentalMetadata: response?.rentalMetadata,
    }
  } catch (error) {
    console.error('Failed to add rental to cart:', error)
    return { success: false, ...extractErrorPayload(error, 'Failed to add rental to cart') }
  }
}
